import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDeleteDevice, useDevice } from '../api/queries'
import { useAuth } from '../auth/AuthProvider'
import { DeviceFilesCard } from '../components/DeviceFilesCard'
import { DeviceForm } from '../components/DeviceForm'
import { DeviceLabel, DeviceQr, deviceUrl } from '../components/DeviceLabel'
import { DeviceHistoryCard } from '../components/DeviceHistoryCard'
import { DeviceReplacedPartsCard } from '../components/ReplacedPartsCard'
import { RelatedManualsCard } from '../components/RelatedManualsCard'
import { DeviceServiceCard, DeviceVisitsCard } from '../components/DeviceServiceCard'
import { SiteContactFields } from '../components/SiteContact'
import { WorkSiteField } from '../components/WorkSite'
import { Card, ErrorBox, Loading } from '../components/ui'
import { formatDate, formatDateTime } from '../lib/format'
import { serviceStatus } from './DevicesPage'

/**
 * Enhedens side: oplysninger, serviceaftale og QR-mærkatet der kan printes.
 * Det er også den side, man lander på efter at have scannet mærkatet.
 */
export function DeviceDetailPage() {
  const { id } = useParams()
  const deviceId = Number(id)
  const { isProjectManager, isAdmin } = useAuth()
  const { data, isLoading, error } = useDevice(deviceId)
  const [editing, setEditing] = useState(false)
  // Slet (administratorer): kun enheder uden historik - serveren siger hvorfor, hvis det ikke kan lade sig gøre.
  const remove = useDeleteDevice()
  const navigate = useNavigate()
  const [deleteError, setDeleteError] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <ErrorBox error={new Error('Enheden findes ikke.')} />

  const device = data.device
  const due = serviceStatus(device)

  if (editing) {
    return (
      <DeviceForm
        device={data}
        onClose={() => setEditing(false)}
        onSaved={() => setEditing(false)}
      />
    )
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card
        actions={
          isProjectManager ? (
            <div className="btn-group">
              <button className="btn btn--sm" onClick={() => setEditing(true)}>
                Rediger
              </button>
              {isAdmin && (
                <button
                  className="btn btn--sm btn--danger"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (!window.confirm(`Slet enheden "${device.name}" (${device.code})? Det kan ikke fortrydes.`)) return
                    setDeleteError(null)
                    remove.mutate(device.id, {
                      onSuccess: () => navigate('/devices'),
                      onError: (err) => setDeleteError(err instanceof Error ? err.message : 'Enheden kunne ikke slettes.')
                    })
                  }}
                >
                  {remove.isPending ? 'Sletter ...' : 'Slet'}
                </button>
              )}
            </div>
          ) : undefined
        }
      >
        {deleteError && <div className="error-box" style={{ marginBottom: 12 }}>{deleteError}</div>}
        <div className="row row--between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 className="page-title" style={{ marginBottom: 4 }}>
              {device.name}
            </h1>
            <div className="muted small">
              {[device.customerName, device.location].filter(Boolean).join(' · ') || 'Ingen placering angivet'}
            </div>

            <div className="form-grid" style={{ marginTop: 16 }}>
              {/* Enheden er det, man åbner efter at have scannet mærkatet - derfra
                  skal telefon og adresse kunne trykkes direkte. */}
              <SiteContactFields contact={data.site} />
              <WorkSiteField
                site={data.workSite}
                label="Enheden står"
                missingText="Adressen kunne ikke findes, så enheden står ikke på kortet. Ret den, hvis den er skrevet forkert."
              />
              <Field label="Kode" value={device.code} />
              <Field label="Type" value={device.deviceType} />
              <Field label="Serienummer" value={device.serialNumber} />
              <Field label="Fabrikat" value={data.manufacturer} />
              <Field label="Model" value={data.model} />
              <Field label="Installeret" value={formatDate(data.installedOn)} />
            </div>

            {data.installedProjectId && (
              <div style={{ marginTop: 12 }}>
                <div className="tiny muted">Installeret under sag</div>
                <Link className="small" to={`/projects/${data.installedProjectId}`}>
                  {data.installedProjectName}
                </Link>
              </div>
            )}

            {data.notes && (
              <div style={{ marginTop: 12 }}>
                <div className="tiny muted">Noter</div>
                <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                  {data.notes}
                </div>
              </div>
            )}
          </div>

          <div style={{ textAlign: 'center' }}>
            <DeviceQr code={device.code} size={140} />
            <div className="tiny muted" style={{ marginTop: 6, letterSpacing: 1 }}>
              {device.code}
            </div>
            <Link className="btn btn--sm" style={{ marginTop: 8 }} to={`/devices/${device.id}/label`}>
              Print mærkat
            </Link>
          </div>
        </div>
      </Card>

      <DeviceServiceCard device={device} due={due} />

      {/* Sammenstillingen af alt, systemet ved om enheden (beslutning 062).
          Den står her - lige under "Start service" - fordi det er de
          spørgsmål, montøren har, mens han står foran anlægget. */}
      <DeviceHistoryCard deviceId={device.id} />

      {/* Hvad der er skiftet paa enheden, paa tvaers af servicesager (beslutning 098). */}
      <DeviceReplacedPartsCard deviceId={device.id} />

      {/* Enhedens egne dokumenter - tavletegninger, datablade (beslutning 109). */}
      <DeviceFilesCard deviceId={device.id} />

      {/* Manualerne til den her slags enhed (beslutning 063). Staar hoejt: det
          er dem, montoeren skal bruge, mens han staar foran anlaegget. */}
      <RelatedManualsCard
        deviceType={device.deviceType}
        title={`Manualer til ${device.deviceType || 'enheden'}`}
      />

      <DeviceVisitsCard deviceId={device.id} />

      <Card title="Mærkat">
        <p className="small muted" style={{ marginTop: 0 }}>
          QR-koden peger på <code>{deviceUrl(device.code)}</code>. Den kan scannes med mobilens eget
          kamera eller inde i appen under Service. Siden kræver login.
        </p>
        <DeviceLabel device={device} />
      </Card>

      <div className="tiny muted">Oprettet {formatDateTime(data.createdAt)}</div>
    </div>
  )
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="tiny muted">{label}</div>
      <div className="small">{value || '-'}</div>
    </div>
  )
}
